import { useEffect, useState } from "react";
import {
  parseStoragePath,
  signedImageUrl,
  SIGNED_URL_TTL_SECONDS,
} from "@reciperoll/core";
import { supabase } from "./supabase";

// Web has no persistent file cache, so a private-bucket image value (a storage path)
// must be signed to a short-lived URL for display. This hook signs on demand and
// caches the result in memory (keyed by the stored value) so the same image isn't
// re-signed by every card, re-signing only when the signed URL nears expiry.
// Direct-renderable values (blob:/data:/http — e.g. a form's local preview or a
// legacy public URL) are returned as-is, synchronously (no flash).

const cache = new Map<string, { url: string; expiresAt: number }>();
const REFRESH_BEFORE_MS = 5 * 60_000; // re-sign when under 5 minutes remain

function cached(stored: string): string | undefined {
  const hit = cache.get(stored);
  return hit && hit.expiresAt - Date.now() > REFRESH_BEFORE_MS ? hit.url : undefined;
}

export function useSignedImageUrl(stored: string | undefined | null): string | undefined {
  const [url, setUrl] = useState<string | undefined>(() => {
    if (!stored) return undefined;
    if (!parseStoragePath(stored)) return stored; // direct-renderable
    return cached(stored);
  });

  useEffect(() => {
    let active = true;
    if (!stored) {
      setUrl(undefined);
      return;
    }
    if (!parseStoragePath(stored)) {
      setUrl(stored); // direct-renderable
      return;
    }
    const hit = cached(stored);
    if (hit) {
      setUrl(hit);
      return;
    }
    signedImageUrl(supabase, stored, SIGNED_URL_TTL_SECONDS).then((u) => {
      if (u) {
        cache.set(stored, { url: u, expiresAt: Date.now() + SIGNED_URL_TTL_SECONDS * 1000 });
      }
      if (active) setUrl(u);
    });
    return () => {
      active = false;
    };
  }, [stored]);

  return url;
}
